import {
  PerAgentOfferAcceptRateFragment,
  PerAgentTransactionStatsFragment,
  PerOfficeOfferAcceptRateFragment,
  PerOfficeTransactionStatsFragment,
} from '@client/graphql/__generated__/types';
import EventEmitter from 'eventemitter3';
import { range } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { formatPercent, formatPrice } from '~/common/utils/formatter';
import { OrderBy } from '~/services/document/common/GridTable/GridTable';

interface UseAgentListOptions {
  scrollElement?: HTMLDivElement | null;
  offerAcceptRate?: (
    | PerAgentOfferAcceptRateFragment
    | PerOfficeOfferAcceptRateFragment
  )[];
  transactionStats?: (
    | PerAgentTransactionStatsFragment
    | PerOfficeTransactionStatsFragment
  )[];
}

interface ColumnConfig {
  key: string;
  title: string;
  orderByKey?: string;
  formatter?: (value: number | null) => string;
}

// Base columns that are always shown
const BASE_COLUMNS: ColumnConfig[] = [
  {
    key: 'name',
    title: 'Name',
  },
  {
    key: 'submitted_offers',
    title: 'Submitted',
    orderByKey: 'submitted_offers',
  },
  {
    key: 'accepted_offers',
    title: 'Accepted',
    orderByKey: 'accepted_offers',
  },
  {
    key: 'total_volume',
    title: '$ Accepted',
    orderByKey: 'total_volume',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPrice(value || 0) || 'N/A',
  },
  {
    key: 'offer_accept_rate',
    title: 'Accept Rate',
    orderByKey: 'offer_accept_rate',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPercent(value || 0) || 'N/A',
  },
];

// Group all average-related columns
const STATS_COLUMNS: ColumnConfig[] = [
  {
    key: 'average_purchase_price',
    title: 'Avg Purchase Price',
    orderByKey: 'average_purchase_price',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPrice(value || 0) || 'N/A',
  },
  {
    key: 'average_bid_ask',
    title: 'Avg Sale/List',
    orderByKey: 'average_bid_ask',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPercent(value || 0) || 'N/A',
  },
  {
    key: 'average_ddf_ratio',
    title: 'Avg DDF%',
    orderByKey: 'average_ddf_ratio',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPercent(value || 0) || 'N/A',
  },
  {
    key: 'average_termination_fee_ratio',
    title: 'Avg Termination Fee%',
    orderByKey: 'average_termination_fee_ratio',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : formatPercent(value || 0) || 'N/A',
  },
  {
    key: 'average_dd_days',
    title: 'Avg DD Days',
    orderByKey: 'average_dd_days',
    formatter: (value: number | null) =>
      value === null ? 'N/A' : `${value || 0} days`,
  },
  {
    key: 'average_offer_to_close_days',
    title: 'Avg Offer to Close Days',
    orderByKey: 'average_offer_to_close_days',
    formatter: (value: number | null) =>
      value === null || value === 0 ? 'N/A' : `${value} days`,
  },
];

export const usePerformanceList = ({
  offerAcceptRate,
  transactionStats,
  scrollElement,
}: UseAgentListOptions) => {
  const [orderBy, setOrderBy] = useState<OrderBy | null>({
    key: 'total_volume',
    order: 'desc',
  });
  const visibleRowsRef = useRef<string[]>(range(30).map((i) => String(i)));
  const extendedVisibleRowsRef = useRef<string[]>(
    range(30).map((i) => String(i))
  );
  const visibleEventEmitter = useMemo(() => new EventEmitter(), []);

  const intersectionObserver = useMemo(() => {
    if (!scrollElement) return null;

    const onIntersection = (entries: IntersectionObserverEntry[]) => {
      const newVisibleRows = entries.filter(
        (entry) => entry.intersectionRatio > 0
      );
      const newHiddenRows = entries.filter(
        (entry) => entry.intersectionRatio === 0
      );

      const prevFiltered = visibleRowsRef.current.filter(
        (index) =>
          !newHiddenRows.some(
            (entry) =>
              (entry.target as HTMLDivElement).dataset.index === String(index)
          )
      );

      visibleRowsRef.current = Array.from(
        new Set([
          ...prevFiltered,
          ...newVisibleRows.map(
            (entry) => (entry.target as HTMLDivElement).dataset.index
          ),
        ] as string[])
      );

      const max = Math.max(...visibleRowsRef.current.map((i) => parseInt(i)));
      const min = Math.min(...visibleRowsRef.current.map((i) => parseInt(i)));

      extendedVisibleRowsRef.current = [
        ...range(min - 5, min).map((i) => String(i)),
        ...visibleRowsRef.current,
        ...range(max + 1, max + 10).map((i) => String(i)),
      ];

      visibleEventEmitter.emit(
        'visibleRowsUpdated',
        extendedVisibleRowsRef.current
      );
    };

    return new IntersectionObserver(onIntersection, {
      root: scrollElement,
      threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
    });
  }, [scrollElement, visibleEventEmitter]);

  const items = useMemo(() => {
    return offerAcceptRate
      ?.map((agent) => {
        const stats = transactionStats?.find((o) => o.name === agent.name);

        return {
          ...agent,
          ...stats,
        };
      })
      .sort((a, b) => {
        if (!orderBy) return b.total_offers - a.total_offers;

        return orderBy.order === 'asc'
          ? (a[orderBy.key] ?? 0) - (b[orderBy.key] ?? 0)
          : (b[orderBy.key] ?? 0) - (a[orderBy.key] ?? 0);
      });
  }, [offerAcceptRate, transactionStats, orderBy]);

  const observeRow = useCallback(
    (ref: HTMLDivElement) => {
      if (ref) {
        intersectionObserver?.observe(ref);
      }
    },
    [intersectionObserver]
  );

  const onOrderBy = useCallback((orderBy: OrderBy) => {
    setOrderBy(orderBy);
  }, []);

  // Hide columns that are all values are null
  const visibleStatsColumns = useMemo(() => {
    const isColumnAllNull = (key: string) => {
      if (!offerAcceptRate) return true;

      return offerAcceptRate.every((agent) => agent[key] === null);
    };

    return STATS_COLUMNS.filter((column) => !isColumnAllNull(column.key));
  }, [offerAcceptRate]);

  return {
    items,
    observeRow,
    visibleEventEmitter,
    onOrderBy,
    orderBy,
    baseColumns: BASE_COLUMNS,
    visibleStatsColumns,
  };
};
